import { hasError } from '@repo/shared/api/helpers'
import { Button } from '@repo/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form'
import { useFormValidator, useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { Language } from '@repo/ui/lang'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/select'
import { Building2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Uploader } from '@/components/beneficiary/FileUploader.tsx'
import { api } from '@/main.tsx'
import { OrganizationCreationFields } from '@/store/spi/api'
import { organizationSchema } from '@/validation-schemas'

export function OrganizationCreatorDialog({
  defaultLanguage,
  onSubmit,
  className,
}: { className?: string } & OrganizationFormProps) {
  const [open, setOpen] = useState<boolean>(false)
  const { t } = useT({ keyPrefix: 'admin.organization.creator.dialog' })
  return (
    <>
      <Button variant="outline" className={className} onClick={() => setOpen(true)}>
        <Building2 className="size-4" />
        {t('cta')}
      </Button>
      {open && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-[480px]">
            <DialogHeader>
              <DialogTitle>{t('title')}</DialogTitle>
              <DialogDescription>{t('description')}</DialogDescription>
            </DialogHeader>
            <OrganizationForm
              defaultLanguage={defaultLanguage}
              onSubmit={(v) => {
                setOpen(false)
                onSubmit(v)
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

type OrganizationFormProps = {
  defaultLanguage: Language
  onSubmit: (p: OrganizationCreationFields) => void
}
function OrganizationForm({ defaultLanguage, onSubmit }: OrganizationFormProps) {
  const { t, tc } = useT({ keyPrefix: 'admin.organization.creator' })

  const defaultValues = useMemo(() => ({ name: '', language: defaultLanguage }), [])

  let loadingSnapshot = false
  const { form, isFormValid, submit, submitOnPressMetaEnter } = useFormValidator({
    defaultValues,
    onSubmit: (values) => {
      onSubmit(values)
      form.reset(defaultValues)
    },
    schema: {
      language: organizationSchema.language,
      name: organizationSchema.name,
      url: organizationSchema.url,
    },
  })

  return (
    <Form {...form}>
      <form onSubmit={submit} onKeyDown={submitOnPressMetaEnter} className="grid gap-4">
        <FormField
          control={form.control}
          name="language"
          render={({ field }) => (
            <FormItem>
              <FormLabel htmlFor="language">{tc('language', { cfl: true })}</FormLabel>
              <FormControl>
                <Select name={field.name} onValueChange={field.onChange} defaultValue={field.value}>
                  <SelectTrigger id="language" ref={field.ref}>
                    <SelectValue placeholder={tc('selectLanguagePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={Language.EN}>
                      <div className="flex items-center">English</div>
                    </SelectItem>
                    <SelectItem value={Language.FR}>
                      <div className="flex items-center">Français</div>
                    </SelectItem>
                    <SelectItem value={Language.ES}>
                      <div className="flex items-center">Español</div>
                    </SelectItem>
                    <SelectItem value={Language.PT}>
                      <div className="flex items-center">Português</div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel htmlFor="name">{tc('name')}</FormLabel>
              <FormControl>
                <Input id="name" placeholder={tc('name')} className="col-span-3" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="url"
          render={({ field }) => (
            <FormItem>
              <FormLabel htmlFor="url">
                {tc('url', { cfl: true })} <span className="text-xs">({tc('optional')})</span>
              </FormLabel>
              <FormControl>
                <Input
                  id="url"
                  placeholder={tc('url')}
                  className="col-span-3"
                  {...field}
                  value={field.value ?? ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="text-primary">{t('description')}</div>
        <Uploader
          processFile={async ({ file, organizationId }) => {
            if (organizationId) return
            const params = {
              language: form.watch('language'),
              name: form.watch('name'),
              url: form.watch('url'),
            }
            loadingSnapshot = true
            return await api.sudo.snapshot.loadSnapshot({
              ...params,
              file: file,
            })
          }}
          onUploaded={(response) => {
            if (hasError(response)) return

            //fixme something 'smarter' ?
            const dateShort = new Date()
              .toISOString()
              .slice(0, 19)
              .replaceAll('_', '')
              .replaceAll(':', '')
              .replaceAll('-', '')
            let url: string
            let fileName: string
            let toastMessage: string
            if (response?.errorsOnLoad && response?.errorsOnLoad.length > 0) {
              fileName = `load-snapshot-${dateShort}.errors.log`
              url = URL.createObjectURL(
                new Blob([response.errorsOnLoad.join('\n')], {
                  type: 'text/plain',
                }),
              )
              toastMessage = `Creation from snapshot failed - see ${fileName} for details`
            } else {
              fileName = `load-snapshot-${dateShort}.logs.json`
              url = URL.createObjectURL(
                new Blob([JSON.stringify(response?.logs, null, 2)], {
                  type: 'application/json;charset=utf-8',
                }),
              )
              toastMessage = `Organization "${response?.processedOrganization.name}" created from snapshot - see ${fileName} for details`
            }

            const a = document.createElement('a')
            a.href = url
            a.download = fileName
            a.click()
            URL.revokeObjectURL(url)

            toast.info(toastMessage)
          }}
        />
        <DialogFooter>
          <Button type="submit" disabled={!isFormValid || loadingSnapshot}>
            {tc('create', { cfl: true })} {form.watch('name')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  )
}
